import { first, isNil } from 'lodash';
import { useContext, useMemo } from 'react';
import { Menu, Submenu, useRootMenuContext } from '@shared/components/Menu';
import { ShareQRCode } from '@shared/components/qrCodes';
import {
  useHandleCopyQr,
  useSharedCardLinkState,
} from '@shared/scenes/cards/CardActionMenu/shareQrCodeOperations';
import { CardChannelContext } from '@shared/scenes/cards/context';
import { useViewTemplates } from '@shared/scenes/workflows/hooks';
import { copyToClipboard } from '@shared/util/clipboard';
import { i18n } from '@shared/util/i18n';
import { usePermission } from '@shared/util/permissions/hooks';
import styles from './ShareSubmenu.module.css';

interface Props {
  entityId: string;
}

export function ShareSubmenu({ entityId }: Props) {
  const { align } = useRootMenuContext();
  const channel = useContext(CardChannelContext);
  const sharedCardLinkPermissions = usePermission({
    action: 'manage',
    subject: 'SharedCardLink',
    channelId: channel?.id,
  });
  // A public, readonly view template corresponds to the backend's
  // `nocode-public-readonly` card UI template context, and its id is the slug
  // used for the public shared card link.
  const { viewTemplates } = useViewTemplates({
    workflowTemplateId: channel?.workflowTemplateId,
    isPublic: true,
    subtype: 'READONLY',
  });
  const nocodeReadonlyPublicTemplate = first(viewTemplates);

  const cardHost = getCardHost();
  const privateCardUrl = useMemo(() => {
    return `${cardHost}/${channel?.slug}/cards/${entityId}`;
  }, [cardHost, channel?.slug, entityId]);

  const { showPublicQrCode, publicLink, onToggleShowPublicQrCode } = useSharedCardLinkState({
    channelId: channel?.id,
    type: 'VIEW_CARD',
    viewTemplateId: nocodeReadonlyPublicTemplate?.id,
    cardId: entityId,
    disabled: sharedCardLinkPermissions.enabled,
  });

  const copyQr = useHandleCopyQr();
  const hasNoCodeReadonlyPublicTemplate = !isNil(nocodeReadonlyPublicTemplate);

  return (
    <Submenu align={align}>
      <Submenu.Trigger>
        <Menu.Item label={i18n('common.share')} />
      </Submenu.Trigger>
      <Submenu.Content testID="share-card-sheet" className={styles.shareSubmenuContent}>
        <ShareQRCode
          privateShare={{
            url: privateCardUrl,
            onCopyQr: copyQr,
            onCopyLink: copyToClipboard,
          }}
          canCreatePublicCode={sharedCardLinkPermissions.enabled && hasNoCodeReadonlyPublicTemplate}
          hasPublicQrCode={showPublicQrCode}
          publicUrl={publicLink?.url}
          onSwitchPublic={onToggleShowPublicQrCode}
          onCopyPublicQr={copyQr}
          onCopyPublicLink={copyToClipboard}
        />
      </Submenu.Content>
    </Submenu>
  );
}

function getCardHost() {
  const appUrl = import.meta.env.VITE_APP_URL;
  if (import.meta.env.DEV && appUrl.includes('localhost')) {
    return 'https://staging.coastapp.com';
  } else {
    return appUrl;
  }
}
